import React, { useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Badge,
  Table,
} from "react-bootstrap";
import {
  FaRocket,
  FaCheckCircle,
  FaExclamationTriangle,
  FaFileAlt,
  FaDownload,
  FaShieldAlt,
  FaLightbulb,
  FaPlus,
  FaTrash,
  FaBoxes,
} from "react-icons/fa";
import Layout from "./Layout";
import axios from "axios";
import "../assets/css/SpBrandProtectionProducts.css";

const SpBrandProtectionProducts = () => {
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api";

  // Estados para configuracion
  const [siteType, setSiteType] = useState("AMAZON_AND_BEYOND");

  // Estados para archivos
  const [productsFile, setProductsFile] = useState(null);

  const [targetAsins, setTargetAsins] = useState([{ asin: "" }]);
  const [targetAsinsText, setTargetAsinsText] = useState("");
  const [inputMode, setInputMode] = useState("list"); // 'list' or 'text'

  // Estados de configuracion de campaign
  const [defaultBid, setDefaultBid] = useState(0.75);
  const [defaultBudget, setDefaultBudget] = useState(15);
  const [campaignState, setCampaignState] = useState("PAUSED");
  const [biddingStrategy, setBiddingStrategy] = useState("SALES_DOWN_ONLY");

  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [creationComplete, setCreationComplete] = useState(false);

  // Bulk actions states
  const [bulkAction, setBulkAction] = useState("");
  const [bulkBudget, setBulkBudget] = useState("");
  const [bulkBid, setBulkBid] = useState("");

  // Manejar carga de archivo de productos
  const handleProductsFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith(".csv")) {
      setProductsFile(file);
      setError(null);
    } else {
      setError("Please select a valid CSV file");
    }
  };

  // Manejar cambio en ASIN objetivo
  const handleTargetAsinChange = (index, value) => {
    const updated = [...targetAsins];
    updated[index].asin = value.toUpperCase();
    setTargetAsins(updated);
  };

  // Add new target ASIN
  const addTargetAsin = () => {
    setTargetAsins([...targetAsins, { asin: "" }]);
  };

  // Delete ASIN objetivo
  const removeTargetAsin = (index) => {
    if (targetAsins.length > 1) {
      const updated = targetAsins.filter((_, i) => i !== index);
      setTargetAsins(updated);
    }
  };

  // Parsear ASINs desde texto
  const parseAsinsFromText = () => {
    const asins = targetAsinsText
      .split(/[\n,;]+/)
      .map((a) => a.trim().toUpperCase())
      .filter((a) => a.length === 10 && /^B0[A-Z0-9]{8}$/.test(a));

    if (asins.length > 0) {
      setTargetAsins(asins.map((asin) => ({ asin })));
      setResult({
        success: true,
        message: `${asins.length} valid ASINs detected`,
      });
    } else {
      setError("No valid ASINs found. Format: B0XXXXXXXXX");
    }
  };

  // Generate product protection campaigns
  const handleGenerateCampaigns = async () => {
    if (!productsFile) {
      setError("You must upload the products file (ASIN, SKU)");
      return;
    }

    // Get valid target ASINs
    const validTargetAsins =
      inputMode === "list"
      ? targetAsins
        .filter((t) => t.asin.trim().length === 10)
        .map((t) => t.asin.trim())
      : targetAsinsText
            .split(/[\n,;]+/)
            .map((a) => a.trim().toUpperCase())
            .filter((a) => a.length === 10);

    if (validTargetAsins.length === 0) {
      setError(
        "You must enter at least one valid target ASIN (10 characters)",
      );
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("products_file", productsFile);
      formData.append("site_type", siteType);
      formData.append("default_bid", defaultBid);
      formData.append("default_budget", defaultBudget);
      formData.append("bidding_strategy", biddingStrategy);
      formData.append("target_asins", JSON.stringify(validTargetAsins));

      const response = await axios.post(
        `${API_URL}/sp-brand-protection-products/generate`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success) {
        const generatedCampaigns = response.data.campaigns || [];

        const campaignsWithSelection = generatedCampaigns.map((campaign) => ({
          ...campaign,
          selected: true,
        }));

        setCampaigns(campaignsWithSelection);

        setResult({
          success: true,
          message: `${generatedCampaigns.length} product protection campaigns generated.`,
          count: generatedCampaigns.length,
        });
      } else {
        setError(response.data.error || "Error generating campaigns");
      }
    } catch (err) {
      console.error("Error:", err);
      setError(
        err.response.data.error || err.message || "Error generating campaigns",
      );
    } finally {
      setLoading(false);
    }
  };

  // Create campaigns en Amazon
  const handleCreateInAmazon = async () => {
    const selectedCampaigns = campaigns.filter((c) => c.selected);

    if (selectedCampaigns.length === 0) {
      setError("You must select at least one campaign to create in Amazon");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const profileId = localStorage.getItem("selectedAccountValue");

      if (!profileId) {
        setError(
          "Please select an account in the selector above before creating campaigns",
        );
        setLoading(false);
        return;
      }

      const response = await axios.post(
        `${API_URL}/sp-brand-protection-products/create-in-amazon`,
        {
          profileId,
          campaigns: selectedCampaigns,
          campaignState,
          siteType,
          biddingStrategy,
        },
      );

      if (response.data.success) {
        const amazonResults = response.data.results || [];
        const errors = response.data.errors || [];

        setResult({
          success: true,
          message: response.data.message,
          count: response.data.campaigns_created,
          amazonResults: amazonResults,
          errors: errors,
        });

        const campaignsForTable = amazonResults.map((result) => ({
          "Campaign Name": result.campaignName,
          "Campaign ID": result.campaignId,
          "Ad Group ID": result.adGroupId,
          "Target ASIN": result.targetAsin,
          Status: "Created in Amazon",
        }));

        setCampaigns(campaignsForTable);
        setCreationComplete(true);
      } else {
        setError(response.data.error || "Error creating campaigns in Amazon");
      }
    } catch (err) {
      console.error("Error:", err);
      setError(
        err.response.data.error ||
          err.message ||
          "Error creating campaigns in Amazon",
      );
    } finally {
      setLoading(false);
    }
  };

  // Toggle seleccion
  const toggleCampaignSelection = (index) => {
    const updatedCampaigns = [...campaigns];
    updatedCampaigns[index].selected = !updatedCampaigns[index].selected;
    setCampaigns(updatedCampaigns);
  };

  const selectAllCampaigns = () => {
    const updatedCampaigns = campaigns.map((c) => ({ ...c, selected: true }));
    setCampaigns(updatedCampaigns);
  };

  const deselectAllCampaigns = () => {
    const updatedCampaigns = campaigns.map((c) => ({ ...c, selected: false }));
    setCampaigns(updatedCampaigns);
  };

  // Apply bulk action
  const applyBulkAction = () => {
    if (!bulkAction) {
      setError("Select a bulk action");
      return;
    }

    const selectedCount = campaigns.filter((c) => c.selected).length;
    if (selectedCount === 0) {
      setError("Select at least one campaign");
      return;
    }

    const updatedCampaigns = campaigns.map((campaign) => {
      if (!campaign.selected) return campaign;

      const updated = { ...campaign };

      switch (bulkAction) {
        case "budget":
          if (bulkBudget && parseFloat(bulkBudget) > 0) {
            updated.Budget = parseFloat(bulkBudget);
          }
          break;
        case "bid":
          if (bulkBid && parseFloat(bulkBid) > 0) {
            updated.Bid = parseFloat(bulkBid);
          }
          break;
        default:
          break;
      }

      return updated;
    });

    setCampaigns(updatedCampaigns);
    setBulkAction("");
    setBulkBudget("");
    setBulkBid("");
    setError(null);
    setResult({
      success: true,
      message: `Action applied to ${selectedCount} campaign(s)`,
      count: selectedCount,
    });
  };

  // Download CSV
  const handleDownloadCampaigns = () => {
    if (campaigns.length === 0) return;

    const headers = Object.keys(campaigns[0]).filter((h) => h !== "selected");
    const csvContent = [
      headers.join(","),
      ...campaigns.map((campaign) =>
        headers
          .map((header) => {
            const value = campaign[header];
            if (typeof value === "string" && value.includes(",")) {
              return `"${value}"`;
            }
            return value;
          })
          .join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `sp_brand_protection_products_${Date.now()}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Reset
  const handleReset = () => {
    setProductsFile(null);
    setTargetAsins([{ asin: "" }]);
    setTargetAsinsText("");
    setCampaigns([]);
    setResult(null);
    setError(null);
    setCreationComplete(false);
  };

  return (
    <Layout>
      <Container fluid className="sp-brand-protection-products-page py-4">
        <Row className="mb-4">
          <Col>
            <h2 className="text-white mb-3">
              <FaBoxes className="me-2" />
              Brand Protection Products - Sponsored Products
            </h2>
            <p className="text-muted">
              Protect your brand by showing your similar products on product
              pages, helping prevent customers from seeing competitors
            </p>
          </Col>
        </Row>

        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            <FaExclamationTriangle className="me-2" />
            {error}
          </Alert>
        )}

        {result && result.success && !creationComplete && (
          <Alert variant="success" onClose={() => setResult(null)} dismissible>
            <FaCheckCircle className="me-2" />
            {result.message}
          </Alert>
        )}

        {/* Configuration Section */}
        {!result?.amazonResults && (
          <>
            {/* Info Banner */}
            <Alert variant="info" className="mb-4">
              <FaShieldAlt className="me-2" />
              <strong>Product Protection:</strong> Manual Targeting •
              Product Targeting • Individual Products (ASINs)
            </Alert>

            {/* SITE Selection */}
            <div className="config-section-card mb-4">
              <h5 className="mb-3">
                <Badge bg="primary" className="me-2">
                  1
                </Badge>
                Select SITE
              </h5>
              <Form.Select
                value={siteType}
                onChange={(e) => setSiteType(e.target.value)}
                className="site-selector"
              >
                <option value="AMAZON_AND_BEYOND">
                  Amazon and Beyond (Multi-Marketplace)
                </option>
                <option value="AMAZON_BUSINESS">
                  Amazon Business (Single Marketplace)
                </option>
              </Form.Select>
            </div>

            {/* Upload Section */}
            <div className="upload-grid">
              <div className={`upload-card-new ${productsFile ? "has-file" : ""}`}
              >
                <div className="card-required-badge">Required</div>
                <Badge bg="primary" className="step-badge">
                  2
                </Badge>
                <div className="upload-icon">
                  <FaFileAlt />
                </div>
                <div className="upload-card-title">Your Products CSV</div>
                <div className="upload-card-desc">
                  Your products that will be shown as suggestions
                  <br />
                  Columns: ASIN, SKU
                </div>
                <label className="w-100 mt-auto">
                  <div className="upload-btn">
                    {productsFile ? "Change File" : "Select File"}
                  </div>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleProductsFileChange}
                    hidden
                  />
                </label>
                {productsFile && (
                  <small className="text-success mt-2">
                    {productsFile.name}
                  </small>
                )}
              </div>
            </div>

            {/* Target ASINs Section */}
            <div className="config-section-card mt-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">
                  <Badge bg="primary" className="me-2">
                    3
                  </Badge>
                  Target ASINs (Pages where ads appear)
                </h5>
                <div className="d-flex gap-2">
                  <Button
                    variant={
                      inputMode === "list" ? "primary" : "outline-primary"
                    }
                    size="sm"
                    onClick={() => setInputMode("list")}
                  >
                    List
                  </Button>
                  <Button
                    variant={
                      inputMode === "text" ? "primary" : "outline-primary"
                    }
                    size="sm"
                    onClick={() => setInputMode("text")}
                  >
                    Text
                  </Button>
                </div>
              </div>

              <p className="text-muted small mb-3">
                Enter the product ASINs where you want your ads to appear
                as product suggestions
              </p>

                    List
                <>
                  {targetAsins.map((target, index) => (
                    <Row key={index} className="mb-2 target-asin-row">
                      <Col md={10}>
                        <Form.Control
                          type="text"
                          placeholder="B0XXXXXXXXX"
                          value={target.asin}
                          onChange={(e) =>
                            handleTargetAsinChange(index, e.target.value)
                          }
                            Text
                          className="asin-input"
                        />
                      </Col>
                      <Col md={2} className="d-flex align-items-center">
                        {targetAsins.length > 1 && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => removeTargetAsin(index)}
                            className="w-100"
                          >
                            <FaTrash />
                          </Button>
                        )}
                      </Col>
                    </Row>
                  ))}
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={addTargetAsin}
                    className="mt-2"
                  >
                    <FaPlus className="me-1" /> Add ASIN
                  </Button>
                </>
              ) : (
                <>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    placeholder="Paste your ASINs here (one per line, or separated by commas)&#10;&#10;Example:&#10;B08N5WRWNW&#10;B09XYZ1234&#10;B07ABC1234"
                    value={targetAsinsText}
                    onChange={(e) => setTargetAsinsText(e.target.value)}
                    className="asins-textarea"
                  />
                  <Button
                    variant="outline-info"
                    size="sm"
                    onClick={parseAsinsFromText}
                    className="mt-2"
                  >
                    <FaLightbulb className="me-1" /> Validate ASINs
                  </Button>
                </>
              )}
            </div>

            {/* Campaign Configuration */}
            <div className="config-section-card mt-4">
              <h5 className="mb-3">
                <Badge bg="primary" className="me-2">
                  4
                </Badge>
                Campaign Configuration
              </h5>
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-muted">
                      Bid Default ($)
                    </Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0.02"
                      value={defaultBid}
                      onChange={(e) =>
                        setDefaultBid(parseFloat(e.target.value))
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-muted">
                      Daily Budget ($)
                    </Form.Label>
                    <Form.Control
                      type="number"
                      step="1"
                      min="1"
                      value={defaultBudget}
                      onChange={(e) =>
                        setDefaultBudget(parseFloat(e.target.value))
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-muted">
                      Bidding Strategy
                    </Form.Label>
                    <Form.Select
                      value={biddingStrategy}
                      onChange={(e) => setBiddingStrategy(e.target.value)}
                    >
                      <option value="SALES_DOWN_ONLY">
                        Dynamic Bids - Down Only
                      </option>
                      <option value="SALES_UP_AND_DOWN">
                        Dynamic Bids - Up and Down
                      </option>
                      <option value="FIXED_BID">Fixed Bids</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-muted">
                      Targeting Type
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value="Manual Targeting"
                      disabled
                      className="bg-dark text-white"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-muted">
                      Targeting Strategy
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value="Product Targeting (ASIN)"
                      disabled
                      className="bg-dark text-info fw-bold"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Generate Button */}
            <div className="generate-section mt-4">
              <button className={`btn-generate-campaigns ${!loading && productsFile && (targetAsins.some((t) => t.asin.trim().length === 10) || targetAsinsText.trim()) ? "ready" : ""}`}
                onClick={handleGenerateCampaigns}
                disabled={
                  loading ||
                  !productsFile ||
                  (!targetAsins.some((t) => t.asin.trim().length === 10) &&
                    !targetAsinsText.trim())
                }
              >
                {loading ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    Generating product protection campaigns...
                  </>
                ) : (
                  <>
                    <FaBoxes />
                    Generate Product Protection Campaigns
                  </>
                )}
              </button>
            </div>
          </>
        )}

        {/* Results Section */}
        {result && campaigns.length > 0 && (
          <Row>
            <Col>
              <Card className="config-section-card border-0 p-0 overflow-hidden">
                <Card.Header className="bg-dark text-white p-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>
                      <FaBoxes className="me-2" />
                      Product Protection Campaigns ({campaigns.length})
                    </h5>
                  </div>

                  <div>
                    {/* Bulk Actions Row */}
                    <div className="mb-3 d-flex justify-content-between align-items-center">
                      <div className="d-flex gap-2 align-items-center">
                        <span className="text-white fw-bold">
                          Bulk actions:
                        </span>
                        <Form.Select
                          size="sm"
                          value={bulkAction}
                          onChange={(e) => setBulkAction(e.target.value)}
                          style={{ width: "200px" }}
                        >
                          <option value="">Select action</option>
                          <option value="budget">Set budget...</option>
                          <option value="bid">Set bid...</option>
                        </Form.Select>

                        {bulkAction === "budget" && (
                          <Form.Control
                            type="number"
                            size="sm"
                            placeholder="New budget"
                            value={bulkBudget}
                            onChange={(e) => setBulkBudget(e.target.value)}
                            style={{ width: "150px" }}
                            min="0"
                            step="0.01"
                          />
                        )}

                        {bulkAction === "bid" && (
                          <Form.Control
                            type="number"
                            size="sm"
                            placeholder="New bid"
                            value={bulkBid}
                            onChange={(e) => setBulkBid(e.target.value)}
                            style={{ width: "150px" }}
                            min="0.02"
                            step="0.01"
                          />
                        )}

                        {bulkAction && (
                          <Button
                            variant="info"
                            size="sm"
                            onClick={applyBulkAction}
                          >
                            Apply
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons Row */}
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                      <div className="d-flex gap-2 align-items-center">
                        <Form.Select
                          size="sm"
                          value={campaignState}
                          onChange={(e) => setCampaignState(e.target.value)}
                          style={{ width: "auto" }}
                          disabled={creationComplete}
                        >
                          <option value="ENABLED">Create as: Enabled</option>
                          <option value="PAUSED">Create as: Paused</option>
                        </Form.Select>

                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={handleDownloadCampaigns}
                        >
                          <FaDownload className="me-1" /> Download CSV
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={handleReset}
                        >
                          <FaRocket className="me-1" /> New Generation
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body
                  className="p-0 table-responsive bg-dark" style={{ maxHeight : "600px", overflowY: "auto" }}
                >
                  <Table
                    hover
                    variant="dark"
                    className="align-middle mb-0"
                    size="sm" style={{ fontSize : "0.85rem" }}
                  >
                    <thead className="text-info sticky-top bg-dark">
                      <tr>
                        <th width="40">
                          <Form.Check
                            type="checkbox"
                            checked={campaigns.every((c) => c.selected)}
                            onChange={(e) =>
                              e.target.checked ? selectAllCampaigns() : deselectAllCampaigns()
                            }
                          />
                        </th>
                        <th width="50">#</th>
                        <th width="300">Campaign Name</th>
                        <th width="100">Your ASIN</th>
                        <th width="100">Target ASIN</th>
                        <th width="80">Bid</th>
                        <th width="80">Budget</th>
                      </tr>
                    </thead>
                    <tbody>
                      {campaigns.map((campaign, idx) => (
                        <tr key={idx}>
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={campaign.selected || false}
                              onChange={() => toggleCampaignSelection(idx)}
                            />
                          </td>
                          <td>{idx + 1}</td>
                          <td>
                            <div
                              className="text-truncate" style={{ maxWidth : "280px" }}
                            >
                              {campaign["Campaign Name"]}
                            </div>
                          </td>
                          <td className="text-success">{campaign.ASIN}</td>
                          <td className="text-info">
                            {campaign["Target ASIN"]}
                          </td>
                          <td>${campaign.Bid}</td>
                          <td>${campaign.Budget}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
                <Card.Footer className="bg-dark border-top border-secondary d-flex justify-content-between p-3">
                  <div className="d-flex gap-2">
                    <Button
                      variant="secondary"
                      onClick={handleReset}
                      className="fw-bold"
                    >
                      ← Cancel
                    </Button>
                  </div>
                  <div className="d-flex gap-3 align-items-center">
                    <div className="text-muted small">
                      Total: {campaigns.length} | Selected:{" "}
                      {campaigns.filter((c) => c.selected).length}
                    </div>
                    <Button
                      variant="info"
                      size="sm"
                      onClick={handleCreateInAmazon}
                      disabled={
                        loading ||
                        creationComplete ||
                        campaigns.filter((c) => c.selected).length === 0
                      }
                      style={{
                        fontWeight: "bold",
                        padding: "6px 20px",
                      }}
                    >
                      {loading ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-1"
                          />
                          Creating in Amazon...
                        </>
                      ) : creationComplete ? (
                        <>
                          <FaCheckCircle className="me-1" /> Campaigns Created
                        </>
                      ) : (
                        <>
                          <FaBoxes className="me-1" /> Create Product
                          Protection Campaigns
                        </>
                      )}
                    </Button>
                  </div>
                </Card.Footer>
              </Card>
            </Col>
          </Row>
        )}

        {/* Requirements Section */}
        {!result && (
          <div className="requirements-section mt-4">
            <span className="requirements-title">
              Brand Protection Products - Requirements
            </span>
            <div className="req-item">
              <strong>Your Products CSV:</strong> Must include 'ASIN' and 'SKU'
              for your products.
            </div>
            <div className="req-item">
              <strong>Target ASINs:</strong> Product pages where you want to
              appear as a suggestion.
            </div>
            <div className="req-item">
              <strong>Targeting:</strong> Manual Targeting with Product
              Targeting (ASIN).
            </div>
            <div className="req-item">
              <strong>Outcome:</strong> When customers visit the target ASINs,
              they will see your products in suggestions.
            </div>
          </div>
        )}
      </Container>
    </Layout>
  );
};

export default SpBrandProtectionProducts;


